import {
  type Decimal,
  decimalToString,
  fromScaled,
  isZeroDecimal,
  toScaled,
  trimDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import { BIG_TEN, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

/**
 * Column multiplication, the way it is written on paper.
 *
 * The decimal points are ignored while multiplying (12.5 × 0.4 becomes 125 × 4) and put
 * back at the end, `scale` places from the right, where `scale` is the total number of
 * decimal places in the two factors. Each digit of the multiplier gives one partial
 * product, built from right to left with carries; the partial products are then added.
 */
export interface DigitStep {
  /** The multiplicand digit being multiplied. */
  digit: number;
  carryIn: number;
  /** digit × multiplier digit + carryIn. */
  product: number;
  /** The digit written down (the ones place of `product`), or all of it at the front. */
  written: string;
  carryOut: number;
  /** The leftmost digit, where the whole product is written. */
  last: boolean;
}

export interface PartialProduct {
  /** The multiplier digit. */
  multiplierDigit: number;
  /** Its place value as a power of ten (0 = ones, 1 = tens …). */
  position: number;
  steps: DigitStep[];
  /** The partial product's digits, before shifting. */
  digits: string;
  /** The partial product including its place-value zeros. */
  value: bigint;
}

export interface LongMultiplication {
  negative: boolean;
  /** Whole-number digits worked on (decimal points removed, no leading zeros). */
  multiplicand: string;
  multiplier: string;
  /** The factors as typed, for display and the "ignore the points" step. */
  multiplicandText: string;
  multiplierText: string;
  /** Decimal places in the answer. */
  scale: number;
  partials: PartialProduct[];
  /** The product as a whole number, point removed. */
  productDigits: string;
  /** The product as text with its point and sign: −6910.4. */
  productText: string;
  /** The same value without trailing fractional zeros (10.00 → 10). */
  productTrimmed: string;
  /** Products that are exactly zero skip the column working. */
  zero: boolean;
}

export const MAX_FACTOR_DIGITS = 25;

const LEADING_ZEROS = /^0+(?=\d)/;

function workingDigits(d: Decimal): string {
  return (d.int + d.frac).replace(LEADING_ZEROS, "");
}

function partialFor(
  multiplicand: string,
  multiplierDigit: number,
  position: number
): PartialProduct {
  const steps: DigitStep[] = [];
  let carry = 0;
  const count = multiplicand.length;
  for (let k = 0; k < count; k++) {
    const digit = Number(multiplicand[count - 1 - k]);
    const product = digit * multiplierDigit + carry;
    const last = k === count - 1;
    steps.push({
      digit,
      carryIn: carry,
      product,
      written: last ? String(product) : String(product % 10),
      carryOut: Math.floor(product / 10),
      last,
    });
    carry = Math.floor(product / 10);
  }
  const digits = steps
    .map((s) => s.written)
    .reverse()
    .join("")
    .replace(LEADING_ZEROS, "");
  return {
    multiplierDigit,
    position,
    steps,
    digits,
    value: BigInt(digits) * BIG_TEN ** BigInt(position),
  };
}

/** Insert a decimal point `scale` places from the right of a digit string. */
export function placePoint(digits: string, scale: number): string {
  if (scale === 0) {
    return digits;
  }
  const padded = digits.padStart(scale + 1, "0");
  const cut = padded.length - scale;
  return `${padded.slice(0, cut)}.${padded.slice(cut)}`;
}

export function multiplyByColumns(
  a: Decimal,
  b: Decimal
): LongMultiplication | { error: "too-long" } {
  if (
    a.int.length + a.frac.length > MAX_FACTOR_DIGITS ||
    b.int.length + b.frac.length > MAX_FACTOR_DIGITS
  ) {
    return { error: "too-long" };
  }
  const multiplicand = workingDigits(a);
  const multiplier = workingDigits(b);
  const scale = a.frac.length + b.frac.length;
  const zero = isZeroDecimal(a) || isZeroDecimal(b);
  const total = BigInt(multiplicand) * BigInt(multiplier);
  const productDigits = total.toString();
  const negative = !zero && a.negative !== b.negative;
  const partials: PartialProduct[] = [];
  if (!zero) {
    for (let k = 0; k < multiplier.length; k++) {
      const digit = Number(multiplier.at(-1 - k));
      if (digit !== 0) {
        partials.push(partialFor(multiplicand, digit, k));
      }
    }
  }
  return {
    negative,
    multiplicand,
    multiplier,
    multiplicandText: decimalToString({ ...a, negative: false }),
    multiplierText: decimalToString({ ...b, negative: false }),
    scale,
    partials,
    productDigits,
    productText: `${negative ? "−" : ""}${placePoint(productDigits, scale)}`,
    productTrimmed: decimalToString(
      trimDecimal(fromScaled(negative ? -total : total, scale))
    ),
    zero,
  };
}

/* --------------------------------------------------------------- estimate */

/** Round |x| to one significant figure, half up. Zero stays zero. */
export function roundToOneSigFig(d: Decimal): Decimal {
  const { value, scale } = toScaled(d);
  if (value === BIG_ZERO) {
    return d;
  }
  const digits = value.toString();
  if (digits.length === 1) {
    return d;
  }
  const lead = Number(digits[0]) + (Number(digits[1]) >= 5 ? 1 : 0);
  const rounded = BigInt(lead) * BIG_TEN ** BigInt(digits.length - 1);
  const result = fromScaled(rounded, scale);
  return { ...result, negative: d.negative };
}

/* ----------------------------------------------------------------- layout */

export type PlaceholderStyle = "zeros" | "blank";

export interface LayoutOptions {
  placeholder: PlaceholderStyle;
  carries: boolean;
}

const GUTTER = 2;

function blank(width: number): string[] {
  return new Array<string>(width).fill(" ");
}

function putRight(line: string[], endIndex: number, text: string): void {
  const start = endIndex - text.length + 1;
  for (let k = 0; k < text.length; k++) {
    const position = (start + k) * 2;
    if (position >= 0 && position < line.length) {
      line[position] = text[k];
    }
  }
}

function pointLine(
  digits: string,
  scale: number
): { text: string; scale: number } {
  const padded = digits.padStart(scale + 1, "0");
  return { text: padded, scale };
}

/** Write a number's digits, right-aligned, with its decimal point in the gap. */
function putNumber(
  line: string[],
  endIndex: number,
  digits: string,
  scale: number
): void {
  const { text } = pointLine(digits, scale);
  putRight(line, endIndex, text);
  if (scale > 0) {
    const pointAfter = endIndex - scale;
    const position = pointAfter * 2 + 1;
    if (position >= 0 && position < line.length) {
      line[position] = ".";
    }
  }
}

function finish(line: string[]): string {
  return line.join("").trimEnd();
}

function typedDigits(text: string): { digits: string; scale: number } {
  const point = text.indexOf(".");
  if (point === -1) {
    return { digits: text, scale: 0 };
  }
  return {
    digits: text.replace(".", ""),
    scale: text.length - point - 1,
  };
}

/** The carries written above a row, or null when the row has none. */
function carryLine(
  partial: PartialProduct,
  last: number,
  width: number
): string | null {
  const row = blank(width);
  let any = false;
  for (let k = 0; k < partial.steps.length - 1; k++) {
    const carry = partial.steps[k].carryOut;
    if (carry > 0) {
      row[(last - partial.position - (k + 1)) * 2] = String(carry);
      any = true;
    }
  }
  return any ? finish(row) : null;
}

function partialLine(
  partial: PartialProduct,
  last: number,
  width: number,
  options: LayoutOptions
): string {
  const row = blank(width);
  const zeros = options.placeholder === "zeros";
  const shown = zeros
    ? partial.digits + "0".repeat(partial.position)
    : partial.digits;
  putRight(row, zeros ? last : last - partial.position, shown);
  return finish(row);
}

/**
 * The multiplication as lines of monospace text: the factors, the partial products
 * (with carries above them if asked) and the sum underneath.
 */
export function layoutMultiplication(
  m: LongMultiplication,
  options: LayoutOptions
): string[] {
  const top = typedDigits(m.multiplicandText);
  const bottom = typedDigits(m.multiplierText);
  const productShown = Math.max(m.productDigits.length, m.scale + 1);
  const widest = m.partials.reduce(
    (max, p) => Math.max(max, p.digits.length + p.position),
    0
  );
  const columns = Math.max(
    productShown,
    top.digits.length,
    bottom.digits.length,
    widest
  );
  const width = columns * 2 - 1;
  const gutter = " ".repeat(GUTTER);
  const rule = `${"─".repeat(GUTTER)}${"─".repeat(width)}`;
  const last = columns - 1;

  const first = blank(width);
  putNumber(first, last, top.digits, top.scale);
  const second = blank(width);
  putNumber(second, last, bottom.digits, bottom.scale);
  const lines = [
    `${gutter}${finish(first)}`,
    `×${" ".repeat(GUTTER - 1)}${finish(second)}`,
    rule,
  ];
  if (m.zero) {
    const row = blank(width);
    putNumber(row, last, m.productDigits, m.scale);
    return [...lines, `${gutter}${finish(row)}`];
  }
  for (const partial of m.partials) {
    const carryText = options.carries ? carryLine(partial, last, width) : null;
    if (carryText !== null) {
      lines.push(`${gutter}${carryText}`);
    }
    lines.push(`${gutter}${partialLine(partial, last, width, options)}`);
  }
  if (m.partials.length > 1) {
    lines.push(rule);
  }
  const answer = blank(width);
  putNumber(answer, last, m.productDigits, m.scale);
  if (m.partials.length > 1 || m.scale > 0) {
    lines.push(`${gutter}${finish(answer)}`);
  }
  return lines;
}
